// ProjectsListPage — lijst van alle projecten met zoek + "+" naar nieuw project.
// Rendert als tabel op desktop, als cards op mobiel (handmatig; vendor-tabel
// is beschikbaar maar we doen 't hier simpel voor controle over kolom-labels).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Fab,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Alert,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../components/AppLayout.jsx';
import { api, endpoints } from '../api/client.js';

const STATUS_META = {
    draft: { label: 'Concept', color: 'default' },
    in_progress: { label: 'In uitvoering', color: 'warning' },
    completed: { label: 'Afgerond', color: 'success' },
    signed: { label: 'Getekend', color: 'success' },
};

function statusChip(status) {
    const meta = STATUS_META[status] || { label: status || 'Onbekend', color: 'default' };
    return <Chip label={meta.label} size="small" color={meta.color} />;
}

function formatDate(iso) {
    if (!iso) { return '-'; }
    try {
        return new Date(iso).toLocaleString('nl-NL', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
}

export default function ProjectsListPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.get(endpoints.projects);
            setProjects(Array.isArray(data) ? data : (data?.projects || []));
        } catch (err) {
            setError(err?.message || t('common.error'));
            setProjects([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!search.trim()) { return projects; }
        const q = search.toLowerCase();
        return projects.filter(p =>
            (p.customer_name || '').toLowerCase().includes(q)
            || (p.ref || p.zbs_ref || '').toLowerCase().includes(q)
            || (p.install_address || '').toLowerCase().includes(q)
        );
    }, [projects, search]);

    return (
        <AppLayout>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Typography variant="h1">{t('projects.title')}</Typography>
                {isDesktop && (
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/projects/new')}
                    >
                        {t('projects.new')}
                    </Button>
                )}
            </Stack>

            <TextField
                placeholder={t('projects.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 3, maxWidth: 420 }}
            />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && !loading && (
                <Alert severity="error" sx={{ mb: 2 }} action={
                    <Button onClick={load} size="small">{t('common.retry')}</Button>
                }>
                    {error}
                </Alert>
            )}

            {!loading && !error && filtered.length === 0 && (
                <Alert severity="info">{t('projects.empty')}</Alert>
            )}

            {!loading && !error && filtered.length > 0 && (
                isDesktop ? (
                    <TableContainer component={Card} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 64 }}></TableCell>
                                    <TableCell>{t('projects.ref')}</TableCell>
                                    <TableCell>{t('projects.client')}</TableCell>
                                    <TableCell>{t('projects.status')}</TableCell>
                                    <TableCell>{t('projects.updated')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map(p => (
                                    <TableRow
                                        key={p.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/projects/${p.id}/wizard`)}
                                    >
                                        <TableCell sx={{ py: 1 }}>
                                            <Avatar
                                                variant="rounded"
                                                src={p.cover_photo_id ? `/api/photos/${p.cover_photo_id}` : undefined}
                                                sx={{ width: 48, height: 48, bgcolor: 'grey.100', color: 'grey.500' }}
                                            >
                                                <ImageIcon />
                                            </Avatar>
                                        </TableCell>
                                        <TableCell>
                                            <code>{p.ref || p.zbs_ref || p.id}</code>
                                        </TableCell>
                                        <TableCell>
                                            <strong>{p.customer_name || '-'}</strong>
                                            {p.install_address && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {p.install_address}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{statusChip(p.status, t)}</TableCell>
                                        <TableCell>{formatDate(p.updated_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Stack spacing={1.5}>
                        {filtered.map(p => (
                            <Card key={p.id} variant="outlined">
                                <CardActionArea onClick={() => navigate(`/projects/${p.id}/wizard`)}>
                                    <CardContent>
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            justifyContent="space-between"
                                            alignItems="flex-start"
                                        >
                                            <Avatar
                                                variant="rounded"
                                                src={p.cover_photo_id ? `/api/photos/${p.cover_photo_id}` : undefined}
                                                sx={{ width: 72, height: 72, bgcolor: 'grey.100', color: 'grey.500', flexShrink: 0 }}
                                            >
                                                <ImageIcon />
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                >
                                                    {p.ref || p.zbs_ref || p.id}
                                                </Typography>
                                                <Typography variant="h4" component="div" sx={{ my: 0.5 }}>
                                                    {p.customer_name || '-'}
                                                </Typography>
                                                {p.install_address && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {p.install_address}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    {t('projects.updated')}: {formatDate(p.updated_at)}
                                                </Typography>
                                            </Box>
                                            <Box>{statusChip(p.status, t)}</Box>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Stack>
                )
            )}

            {!isDesktop && (
                <Fab
                    color="primary"
                    aria-label={t('projects.new')}
                    sx={{ position: 'fixed', right: 16, bottom: 16 }}
                    onClick={() => navigate('/projects/new')}
                >
                    <AddIcon />
                </Fab>
            )}
        </AppLayout>
    );
}
